'use client';

import { HTTP_REQUEST } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { fetcher } from '@/lib/Requester';
import { PriceData, PriceDataContainer, Product } from '@/models/Product';
import { ShoppingCart } from '@/icons/ShoppingCart';
import { formatNumberToPriceText } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { BreadcrumbView } from './Breadcrumb';
import ProductOrderOptions from './ProductOrderOptions';
import { Button } from './ui/button';
import SmartImage from './SmartImage';
import ProductImageGallery from './ProductImageGallery';

export function ProductDetailView({
  product,
  recommendedProducts = [],
}: {
  product: Product;
  recommendedProducts?: Product[];
}) {
  const [activeProduct, setActiveProduct] = useState<Product>(product);
  const [pricesData, setPricesData] = useState<PriceData | undefined>(undefined);
  const [forceCampaign, setForceCampaign] = useState<string | undefined>(undefined);
  const [selectedRecommendedIds, setSelectedRecommendedIds] = useState<Set<number>>(new Set());

  const openReviews = () => {
    document
      .getElementById('reviews-trigger')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    // When navigating client-side between product pages, this component can remain mounted.
    // Keep state in sync with the new `product` prop and refetch prices.
    setActiveProduct(product);
    setSelectedRecommendedIds(new Set());
    const onlySimple = false;
    // if (pathname === '/hediye-202506') {
    //   setForceCampaign('HDY202506');
    //   onlySimple = true;
    // } else {
    //   const campaignName = getCampaignName();
    //   console.log('campaignName', campaignName);
    //   if (campaignName === 'hediye-202506') {
    //     setForceCampaign('HDY202506');
    //     onlySimple = true;
    //   }
    // }

    const getPriceData = async ({ productIds }: { productIds: number[] }) => {
      try {
        const params = {};

        const response = await fetcher<PriceDataContainer>({
          params: {},
          cacheKey: '',
          endpoint:
            Endpoints.PRICES + '?' + productIds.map((pid) => 'productIds[]=' + pid).join('&'),
          method: HTTP_REQUEST.GET,
          cacheEnabled: false,
        });

        if (onlySimple && response?.items) {
          Object.keys(response.items).forEach((key) => {
            console.log('kk', key, response.items[key].price);
            response.items[key].price = 0;
          });
        }

        setPricesData(response?.items);
      } catch (e) {
        setPricesData(undefined);
      }
    };

    let productIds = [product.id];
    if (product.otherOptions) {
      productIds = productIds.concat(product.otherOptions.map((op) => op.id));
    }

    getPriceData({ productIds });
  }, [product.id]);

  return (
    <>
      <BreadcrumbView
        breadcrumb={activeProduct.breadcrumb ?? []}
        className="px-4 md:px-0"
        includeJsonLd={false}
      />
      <div className="flex flex-col px-4 md:px-0 min-[1020px]:grid min-[1020px]:grid-cols-[500px_1fr] gap-6 w-full max-w-[1240px] mx-auto">
        <div>
          <ProductImageGallery
            images={activeProduct.images}
            reviewData={activeProduct.reviewData}
            productId={activeProduct.id}
            productTitle={activeProduct.title}
            productUrl={activeProduct.url}
            productImageUrl={activeProduct.imageUrl}
          />
        </div>
        <div>
          <ProductOrderOptions
            product={product}
            activeProduct={activeProduct}
            onActiveProductChanged={(baseProduct: Product) => setActiveProduct(baseProduct)}
            pricesData={pricesData}
            forceCampaign={forceCampaign}
            openReviews={openReviews}
            recommendedProducts={recommendedProducts}
            selectedRecommendedIds={selectedRecommendedIds}
            setSelectedRecommendedIds={setSelectedRecommendedIds}
          />
        </div>
      </div>

      {recommendedProducts.length > 0 && (
        <div className="px-4 md:px-0 w-full max-w-[1240px] mx-auto mt-4">
          <div className="w-full rounded-xl border border-[#EDEDF0] bg-white p-4">
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Birlikte Önerilen Ürünler
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {recommendedProducts.map((rp) => {
                const imgSrc = rp.imageUrl || rp.images?.[0]?.imageUrl;
                const isSelected = selectedRecommendedIds.has(rp.id);
                const disabled = rp.inStock !== 1;
                const effectivePrice = pricesData?.[rp.id]?.price ?? rp.price;
                return (
                  <div
                    key={rp.id}
                    className="flex-shrink-0 w-[180px] bg-gray-50 rounded-xl border border-gray-100 overflow-hidden"
                  >
                    <div className="relative w-full aspect-[214/244] overflow-hidden bg-white">
                      {imgSrc ? (
                        <SmartImage
                          src={imgSrc}
                          alt={rp.title}
                          width={180}
                          height={240}
                          imageType="custom"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ShoppingCart className="size-6" />
                        </div>
                      )}
                    </div>
                    <div className="p-2.5 flex flex-col gap-2 min-h-[132px]">
                      <div className="text-xs font-medium text-gray-700 line-clamp-2 leading-tight">
                        {rp.title}
                      </div>

                      <div className="mt-auto flex flex-col gap-2">
                        <div className="text-sm font-bold text-hzc_purple_600">
                          {formatNumberToPriceText(effectivePrice)} TL
                        </div>
                        <Button
                          variant={isSelected ? 'secondary' : 'default'}
                          className="w-full rounded-lg"
                          disabled={disabled}
                          onClick={() => {
                            setSelectedRecommendedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(rp.id)) next.delete(rp.id);
                              else next.add(rp.id);
                              return next;
                            });
                          }}
                        >
                          {isSelected ? 'Eklendi' : 'Ekle'}
                        </Button>
                        {disabled && <div className="text-[11px] text-gray-500">Stokta yok</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
